import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  DialogTitle,
} from "@mui/material";
import IAlert from "../../../types/Alert";
import FacturacionService from "../../../services/FacturacionService";
import ConfigUtils from "../../../utils/Config";
import IServicio from "../../../types/Servicio";
import * as constantes from "../../../common/Constantes";
import { buildFacturaPayload, IFacturaErrors, IFacturaRectificativaPayload, validateFactura } from "../../../utils/Factura";
import { useNavigate } from "react-router-dom";
import LogService from "../../../services/LogService";
import ILog from "../../../types/Log";
import DetalleFactura, { IFacturaData } from "./DetalleFactura";
import DetalleFacturaParcial from "./DetalleFacturaParcial";
import DetalleNoFacturable from "./DetalleNoFacturable";
import FacturarForm from "./FacturarForm";
import CabeceraFactura from "./CabeceraFactura";
import { EstadoFacturacion, getEstadoFacturacion } from "../common/EstadoFacturacionIcon";
import { MetodoPagoCodigo } from "../../../common/Constantes";
import PacientesUtils from "../../../utils/Pacientes";
import { Moment } from "moment";
import moment from "moment";
import ICita from "../../../types/Cita";
import IPaciente from "../../../types/Paciente";
import { IFacturaCompleta } from "./DetalleFacturaComplementaria";
import SelectorEntidadFacturacion, { esFacturacionMultiEntidad } from "../common/SelectorEntidadFacturacion";
import CambiarEntidadDialog from "../common/CambiarEntidadDialog";
import { AvisoEliminacion } from "../common/EliminarFacturaAnulada";

export interface IFacturaMetodoPago {
  nombre: string;
  codigo: MetodoPagoCodigo;
  fechaPago: Moment;
}

export interface IFacturaResponse {
  id: string;
  factura?: IFacturaData;
  facturaParcial?: IFacturaData;
  facturaComplementaria?: IFacturaData;
  cita: ICita;
  paciente: IPaciente;
  tipoFacturacion: string;
  motivoNoFacturable?: string;
}

interface IFacturarCitaDialogProps {
  open: boolean;
  tipo: 'activa' | 'original';
  citaId: string;
  pacienteId: string;
  onClose: () => void;
  onServicioActualizado: (updatedServicio: { _id: string; estado: EstadoFacturacion, servicio?: string, modalidad?: string, total?: number, fechaPago?: string | null, numero?: string, sinAviso?: boolean }) => void;
  onFacturaRectificada?: (factura: IFacturaCompleta, pacienteId: string) => void;
  onRectificativaActualizada?: (updatedFactura: { _id: string; estado: EstadoFacturacion, servicio?: string, modalidad?: string, total?: number, fechaPago?: string | null }) => void;
  onMotivoActualizado?: (nuevoMotivo: string) => void;
  onAnularFactura?: (_id: string, motivoAnulacion: string, devolverSesiones?: boolean, anulacion?: IFacturaCompleta) => void;
  onEntidadCambiada?: (facturaAnuladaId: string, facturaNueva: IFacturaCompleta, anulacion?: IFacturaCompleta | null) => void;
  // Se llama al eliminar una factura anulada sin número (el servicio queda sin factura). Si no se informa, se avisa con
  // onServicioActualizado (estado "sin factura").
  onFacturaEliminada?: (facturaId: string, aviso: AvisoEliminacion) => void;
}

const FacturarCitaDialog = ({ open, tipo, citaId, pacienteId, onClose, onServicioActualizado, onFacturaRectificada, onRectificativaActualizada, onMotivoActualizado, onAnularFactura, onEntidadCambiada, onFacturaEliminada }: IFacturarCitaDialogProps) => {
  const history = useNavigate();
  const [alert, setAlert] = useState<IAlert>({ open: false, severity: "info", msg: "" });
  const [selectedServicio, setSelectedServicio] = useState<IServicio | undefined>(undefined);
  const [tipoPago, setTipoPago] = useState<"total" | "parcial">("total");
  const [facturable, setFacturable] = useState<boolean>(true);
  const [motivoNoFacturable, setMotivoNoFacturable] = useState<string>("");
  const [servicio, setServicio] = useState<string>("");
  const [concepto, setConcepto] = useState<string>("");
  const [precio, setPrecio] = useState<number>(0);
  const [impuesto, setImpuesto] = useState<number>(0);
  const [confirmNoFacturable, setConfirmNoFacturable] = useState(false);
  const [metodoPago, setMetodoPago] = useState<IFacturaMetodoPago | undefined>(undefined);
  const [bonoId, setBonoId] = useState("");
  const [facturaPagada, setFacturaPagada] = useState<boolean>(false);
  const [selectedEntidadId, setSelectedEntidadId] = useState<string | null | undefined>(undefined);
  const [entidadBloqueadaPorBono, setEntidadBloqueadaPorBono] = useState(false);
  const [modalCambiarEntidad, setModalCambiarEntidad] = useState(false);
  const [loadingCambiarEntidad, setLoadingCambiarEntidad] = useState(false);

  const [errors, setErrors] = useState<IFacturaErrors>({});
  const [loadingInit, setLoadingInit] = useState<boolean>(true);
  const [facturaData, setFacturaData] = useState<IFacturaResponse | null>(null);
  const [precioTotal, setPrecioTotal] = useState(0);
  const [inputPrecioBase, setInputPrecioBase] = useState(selectedServicio?.precio ? selectedServicio.precio.toString().replace(".", ",") : "0,00");

  const [editFactura, setEditFactura] = useState<boolean>(false);
  const [numeroFactura, setNumeroFactura] = useState<string>("");
  const [fechaFactura, setFechaFactura] = useState<string>("");

  const [openFacturaInfo, setOpenFacturaInfo] = useState(false);
  const [infoMessage, setInfoMessage] = useState("");
  const [loadingAction, setLoadingAction] = useState(false);
  const [tieneComplementaria, setTieneComplementaria] = useState(false);

  const [rectificarFactura, setRectificarFactura] = useState<boolean>(false);
  const [facturaRectificar, setFacturaRectificar] = useState<IFacturaData | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [nuevaFacturaRectificativa, setNuevaFacturaRectificativa] = useState<IFacturaRectificativaPayload | null>(null);
  const [esRectificativa, setEsRectificativa] = useState(false);

  const [bonoOriginalId, setBonoOriginalId] = useState<string | null>(null);
  const [modalConfirm, setModalConfirm] = useState(false);
  const [accionPendiente, setAccionPendiente] = useState<((d: boolean) => void) | null>(null);
  const [localRecitificativa, setLocalRectificativa] = useState(undefined);
  const userRole: number = ConfigUtils.getUserRole();

  const handleCloseAlert = () => setAlert({ open: false, severity: "info", msg: "" });

  useEffect(() => {
    if (!open) return;

    setLoadingInit(true);

    const checkFactura = async () => {
      try {
        if (!citaId) return;
        let response;
        if (tipo === "activa") {
          response = await FacturacionService.checkActiveServiceInvoice(citaId);
        } else {
          response = await FacturacionService.checkOriginalServiceInvoice(citaId);
        }
        setFacturaData(response.data || null);
        setLocalRectificativa(response.data.isRectificativa);
      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.clear();
          history(constantes.R_HOME);
        } else {
          const error: ILog = {
            method: "FacturarCitaDialog.Component.checkServiceInvoice",
            level: "error",
            message: e.message,
            meta: e.response,
          };
          LogService.logError(error);
          setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
        }
      } finally {
        setLoadingInit(false);
      }
    };

    checkFactura();
  }, [open, citaId]);

  const handleFacturar = async () => {
    runAction(async () => {
      if (!facturable) {
        setErrors({});
        try {
          const response = await FacturacionService.marcarNoFacturable(ConfigUtils.getUserId(), citaId, pacienteId, motivoNoFacturable);
          setFacturaData(response.data);
          onServicioActualizado({ _id: response.data.servicioId, estado: EstadoFacturacion.NO_FACTURABLE });
          onClose();
          return;
        } catch (e: any) {
          if (e.response.status === 401) {
            localStorage.clear();
            history(constantes.R_HOME);
          } else {
            setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
            return;
          }
        }
      }

      const validationErrors = validateFactura({
        tipoPago, selectedServicio, servicio, concepto, precio, facturable, facturaPagada, metodoPago,
        requiereEntidad: esFacturacionMultiEntidad(),
        entidadSeleccionada: selectedEntidadId,
      });
      setErrors(validationErrors);
      if (Object.keys(validationErrors).length > 0) return;

      const precioFinal = Number(precio.toFixed(2));

      const nuevaFactura = buildFacturaPayload({
        userId: ConfigUtils.getUserId(),
        servicioId: citaId,
        pacienteId: pacienteId,
        servicio,
        concepto,
        precio: precioFinal,
        impuesto,
        tipoFactura: "cita",
        tipoPago,
        facturable,
        metodoPago,
        bonoId,
        devolverSesiones: false,
        entityId: selectedEntidadId
      });

      try {
        const response = await FacturacionService.createFactura(nuevaFactura);
        setFacturaData(response.data);
        let factura;
        if (response.data.tipoFacturacion === "total") {
            factura = response.data.factura;
        } else if (response.data.tipoFacturacion === "parcial") {
            factura = response.data.facturaParcial;
        }
        let estadoFacturacion;
        if (factura.estadoPago === "Pagada") estadoFacturacion = EstadoFacturacion.PAGADA;
        else estadoFacturacion = EstadoFacturacion.PENDIENTE_PAGO;
        onServicioActualizado({ _id: response.data.cita._id, estado: estadoFacturacion, fechaPago: factura.metodoPago?.fechaPago?.toString() ?? null });
        return;
      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.clear();
          history(constantes.R_HOME);
        } else {
          const error: ILog = {
            method: "FacturarCitaDialog.Component.handleFacturar",
            level: "error",
            message: e.message,
            meta: e.response,
          };
          LogService.logError(error);
          setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
        }
      }
    });
  };

  // Deja el formulario sin restos de una acción anterior (editar, rectificar, marcar como no facturable...). Se llama al
  // entrar y al salir de los modos editar/rectificar. No toca `facturable` ni el servicio seleccionado.
  const limpiarFormulario = () => {
    setMotivoNoFacturable("");
    setErrors({});
    setMetodoPago(undefined);
    setFacturaPagada(false);
    setBonoId("");
    setBonoOriginalId(null);
    setEsRectificativa(false);
    setNuevaFacturaRectificativa(null);
    setConfirmOpen(false);
    setTieneComplementaria(false);
  };

  const setIsEditFactura = () => {
    limpiarFormulario();
    if (!facturaData) return;
    const factura = facturaData?.factura ?? facturaData?.facturaParcial ?? facturaData?.facturaComplementaria;
    if (!factura) return;
    
    setFechaFactura(factura.fechaFactura.toString());
    setNumeroFactura(factura.numero ?? "");
    setServicio(factura.servicio);
    setConcepto(factura.conceptos[0].nombre);
    setInputPrecioBase(factura.conceptos[0].precio.toFixed(2).replace(".", ","));
    setPrecio(factura.conceptos[0].precio);
    setImpuesto(factura.impuestos[0].cuota);
    setPrecioTotal(factura.conceptos[0].precio + (factura.conceptos[0].precio * factura.impuestos[0].cuota) / 100);
    setTipoPago(facturaData.tipoFacturacion === "parcial" ? "parcial" : "total");
    if (factura.metodoPago) {
      setFacturaPagada(true);
      const metodoPagoResponse = factura.metodoPago;
      setMetodoPago(
        metodoPagoResponse
          ? {
              ...metodoPagoResponse,
              fechaPago: moment(metodoPagoResponse.fechaPago),
            }
          : undefined
      );
    }
    setTieneComplementaria(!!facturaData.facturaComplementaria);

    if (factura.facturaOriginalId) {
      setEsRectificativa(true);
    }
    setBonoId(factura.bonoId || "")

    if (factura.metodoPago?.codigo === MetodoPagoCodigo.Otro) {
      setBonoOriginalId(factura.bonoId!);
    }

    setEditFactura(true);
  };

  // Otra pantalla cambió la factura mientras esta seguía abierta: se vuelven a pedir sus datos (en vez de pedir al usuario que
  // recargue) y se avisa a la pantalla que lo contiene del estado actual del servicio.
  const recargarPorCambioDeEstado = async (mensaje: string) => {
    try {
      const response = tipo === "activa"
        ? await FacturacionService.checkActiveServiceInvoice(citaId)
        : await FacturacionService.checkOriginalServiceInvoice(citaId);
      setFacturaData(response.data || null);
      setLocalRectificativa(response.data.isRectificativa);
      const actual = response.data.tipoFacturacion === "parcial" ? response.data.facturaParcial : response.data.factura;
      if (actual) {
        onServicioActualizado({
          _id: response.data.cita?._id ?? citaId,
          estado: getEstadoFacturacion(actual.estado, actual.estadoPago),
          fechaPago: actual.metodoPago?.fechaPago?.toString() ?? null,
          sinAviso: true, // el aviso de la recarga lo muestra este diálogo; la pantalla de fondo solo actualiza su estado
        });
      }
      setAlert({ open: true, severity: "warning", msg: mensaje });
    } catch (e: any) {
      setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
    }
  };

  // Factura anulada sin número eliminada: el servicio queda sin factura y se cierra el diálogo.
  const handleFacturaEliminada = (facturaId: string, aviso: AvisoEliminacion) => {
    if (onFacturaEliminada) onFacturaEliminada(facturaId, aviso);
    else if (aviso.facturaVigente) {
      // Era una rectificativa: el servicio conserva su factura original, que vuelve a ser la vigente.
      const vigente = aviso.facturaVigente;
      onServicioActualizado({ _id: citaId, estado: getEstadoFacturacion(vigente.estado, vigente.estadoPago), fechaPago: vigente.metodoPago?.fechaPago?.toString() ?? null });
    } else onServicioActualizado({ _id: aviso.servicioId ?? citaId, estado: EstadoFacturacion.SIN_FACTURA });
    onClose();
  };

  const handleCambiarEntidad = async (nuevaEntidadId: string | null) => {
    const facturaActual = facturaData?.factura ?? facturaData?.facturaParcial;
    if (!facturaActual) return;
    try {
      setLoadingCambiarEntidad(true);
      const response = await FacturacionService.cambiarEntidadFactura(facturaActual._id, {
        userId: ConfigUtils.getUserId(),
        entidadNuevaId: nuevaEntidadId,
        estadoEsperado: facturaActual.estado,
      });
      const nuevaFacturaRaw = response.data.factura;
      onEntidadCambiada?.(facturaActual._id, nuevaFacturaRaw, response.data.anulacion);
      setFacturaData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          // Se sustituye por la factura nueva completa (no se fusiona): la nueva no trae número ni fecha de emisión,
          // y al fusionar se conservarían los de la factura anulada.
          factura: prev.factura ? nuevaFacturaRaw : prev.factura,
          facturaParcial: prev.facturaParcial ? nuevaFacturaRaw : prev.facturaParcial,
        };
      });
      setModalCambiarEntidad(false);
      setAlert({ open: true, severity: "success", msg: "Entidad de facturación cambiada correctamente" });
    } catch (e: any) {
      if (e.response?.status === 409) {
        setModalCambiarEntidad(false);
        await recargarPorCambioDeEstado(e.response.data.message);
      } else if (e.response?.status === 401) {
        localStorage.clear();
        history(constantes.R_HOME);
      } else {
        const error: ILog = { method: "FacturarCitaDialog.Component.cambiarEntidad", level: "error", message: e.message, meta: e.response };
        LogService.logError(error);
        setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
      }
    } finally {
      setLoadingCambiarEntidad(false);
    }
  };

  const setIsRectificarFactura = (factura: IFacturaData) => {
    limpiarFormulario();
    setFacturaRectificar(factura);
    setFechaFactura(factura.fechaFactura.toString());
    setNumeroFactura(factura.numero ?? "");
    setServicio(factura.servicio);
    setConcepto(factura.conceptos[0].nombre);
    setInputPrecioBase(factura.conceptos[0].precio.toFixed(2).replace(".", ","));
    setPrecio(factura.conceptos[0].precio);
    setImpuesto(factura.impuestos[0].cuota);
    setPrecioTotal(factura.conceptos[0].precio + (factura.conceptos[0].precio * factura.impuestos[0].cuota) / 100);
    setTipoPago(facturaData?.tipoFacturacion! as "total" | "parcial");
    if (factura.metodoPago) {
      setFacturaPagada(true);
      const metodoPagoResponse = factura.metodoPago;
      setMetodoPago(
        metodoPagoResponse
          ? {
              ...metodoPagoResponse,
              fechaPago: moment(metodoPagoResponse.fechaPago),
            }
          : undefined
      );
    }
    setBonoId(factura.bonoId || "");
    if (factura.metodoPago?.codigo === MetodoPagoCodigo.Otro) {
      setBonoOriginalId(factura.bonoId!);
    }
    setTieneComplementaria(false);
    setRectificarFactura(true);
  };

  const handleEditFactura = async (devolverSesiones: boolean = false) => {
    runAction(async () => {
      if (!facturable) {
        setErrors({});
        try {
          const response = await FacturacionService.marcarNoFacturable(ConfigUtils.getUserId(), citaId, pacienteId, motivoNoFacturable);
          setFacturaData(response.data);
          onServicioActualizado({ _id: response.data.servicioId, estado: EstadoFacturacion.NO_FACTURABLE });
          onClose();
          return;
        } catch (e: any) {
          if (e.response.status === 401) {
            localStorage.clear();
            history(constantes.R_HOME);
          } else {
            setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
            return;
          }
        }
      }

      const validationErrors = validateFactura({ tipoPago, selectedServicio, servicio, concepto, precio: bonoId ? 1 : precio, facturable, facturaPagada, metodoPago, isEdit: true });
      setErrors(validationErrors);
      if (Object.keys(validationErrors).length > 0) return;

      const precioFinal = Number(precio.toFixed(2));
      const editFactura = buildFacturaPayload({
        userId: ConfigUtils.getUserId(),
        servicioId: citaId,
        pacienteId: pacienteId,
        servicio,
        concepto,
        precio: precioFinal,
        impuesto,
        tipoFactura: "cita",
        tipoPago,
        facturable,
        metodoPago,
        bonoId,
        devolverSesiones
      });
      try {
        let facturaId: string | undefined;
        if (facturaData?.tipoFacturacion === "total" || facturaData?.tipoFacturacion === "no_facturable") {
          facturaId = facturaData.factura?._id;
        } else if (facturaData?.tipoFacturacion === "parcial") {
          facturaId = facturaData.facturaParcial?._id;
        }
        if (!facturaId) {
          setAlert({ open: true, severity: "error", msg: "No se ha podido abrir la factura para editarla" });
          return;
        }
        const response = await FacturacionService.editFactura(facturaId, editFactura);
        setFacturaData(response.data);
        let facturaActiva;
        if (response.data.tipoFacturacion === "total") facturaActiva = response.data.factura;
        else if (response.data.tipoFacturacion === "parcial") facturaActiva = response.data.facturaParcial;
        let estadoFacturacion;
        if (facturaActiva.estadoPago === "Pagada") estadoFacturacion = EstadoFacturacion.PAGADA;
        else estadoFacturacion = EstadoFacturacion.PENDIENTE_PAGO;
        onServicioActualizado({ _id: response.data.cita?._id, estado: estadoFacturacion, servicio: facturaActiva.servicio, modalidad: response.data.tipoFacturacion, total: facturaActiva.importeTotal, fechaPago: facturaActiva.metodoPago?.fechaPago?.toString() ?? null });
        setEditFactura(false);
        limpiarFormulario();
        return;
      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.clear();
          history(constantes.R_HOME);
        } else {
          const error: ILog = {
            method: "FacturarCitaDialog.Component.handleEditFactura",
            level: "error",
            message: e.message,
            meta: e.response,
          };
          LogService.logError(error);
          setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
        }
      }
    });
  };

  const onClickEditar = () => {
    const codigoOriginal =
      facturaData?.factura?.metodoPago?.codigo;

    if (
      debePreguntarDevolucion(
        codigoOriginal,
        metodoPago,
        bonoOriginalId,
        bonoId
      )
    ) {
      setAccionPendiente(() => handleEditFactura);
      setModalConfirm(true);
    } else {
      handleEditFactura(false);
    }
  };

  const onClickEditarRectificativa = () => {
    const codigoOriginal =
      facturaData?.factura?.metodoPago?.codigo ||
      facturaData?.facturaParcial?.metodoPago?.codigo;

    if (
      debePreguntarDevolucion(
        codigoOriginal,
        metodoPago,
        bonoOriginalId,
        bonoId
      )
    ) {
      setAccionPendiente(() => handleEditRectificativa);
      setModalConfirm(true);
    } else {
      handleEditRectificativa(false);
    }
  };

  const handleOpenFacturaInfo = () => {
    if (facturaData?.facturaParcial && facturaData.tipoFacturacion === "parcial"){
      setIsEditFactura();
      return;
    }
    // Caso 1: Ya existe factura previa
    if (facturaData?.factura && facturaData.tipoFacturacion === "no_facturable") {
      setInfoMessage("Ya existe una factura para esta cita. Se han recuperado sus datos.");
      setOpenFacturaInfo(true);
      setIsEditFactura();
      return;
    }
    else if (!facturaData?.factura) {
      // Caso 2: No existe factura previa
      setInfoMessage("No hay factura previa. Se creará una nueva con fecha de hoy.");
      setOpenFacturaInfo(true);

      setFacturaData((prev: IFacturaResponse | null) =>
        prev ? { ...prev, tipoFacturacion: "" } : prev
      );
      setFacturable(true);
    }
    setIsEditFactura();
  };

  const validateRectificativa = (devolverSesiones: boolean = false) => {
    setErrors(prev => ({ ...prev, general: undefined }));
    
    const validationErrors = validateFactura({
      tipoPago,
      selectedServicio,
      servicio,
      concepto,
      precio: bonoId ? 1 : precio,
      facturable: true,
      facturaPagada,
      metodoPago,
      isEdit: true
    }); 

    setErrors(validationErrors);  

    if (Object.keys(validationErrors).length > 0) return;

    const original = facturaRectificar!;
    const mismaFechaPago =
    original.metodoPago?.fechaPago &&
    metodoPago?.fechaPago &&
      moment(original.metodoPago.fechaPago).isSame(
      moment(metodoPago.fechaPago),
      'day'
    );
    const cambiosFiscales =
      original.servicio !== servicio ||
      original.conceptos[0].nombre !== concepto ||
      original.conceptos[0].precio !== precio ||
      original.impuestos[0].cuota !== impuesto ||
      (original.bonoId ?? "") !== bonoId;

    const cambioPago =
      original.metodoPago?.nombre !== metodoPago?.nombre ||
      original.metodoPago?.codigo !== metodoPago?.codigo ||
      !mismaFechaPago;

    if (!cambiosFiscales && !cambioPago) {
      setErrors(prev => ({ ...prev, general: "No se han realizado cambios respecto a la factura original" }));
      return;
    }

    if (!cambiosFiscales && cambioPago) {
      setErrors(prev => ({ ...prev, general: "Solo has cambiado el método o la fecha de pago — usa \"Corregir cobro\" en vez de crear una rectificativa" }));
      return;
    }

    const payload: IFacturaRectificativaPayload = {
      userId: ConfigUtils.getUserId(),
      pacienteId: pacienteId,
      servicio,
      conceptos: [{
        nombre: concepto,
        cantidad: 1,
        precio: precio
      }],
      impuestos: [{
        tipo: "IVA",
        base: precio,
        cuota: impuesto
      }],
      tipoPago: tipoPago,
      tipoFactura: original.tipo,
      metodoPago: metodoPago
      ? {
          ...metodoPago,
          nombre: bonoId && bonoId !== "" ? "Bono" : metodoPago.nombre
        }
      : undefined,
      facturaOriginalId: original.numero ?? "",
      bonoId,
      devolverSesiones
    };  

    setNuevaFacturaRectificativa(payload);
    setConfirmOpen(true);
  };

  const handleConfirmRectificativa = async () => {
    runAction(async () => {
      if (!nuevaFacturaRectificativa) return;
      setConfirmOpen(false);
      
      try {
        const response = await FacturacionService.createFacturaRectificativa(nuevaFacturaRectificativa);
        if (onFacturaRectificada) onFacturaRectificada(response.data.factura, pacienteId);
        setFacturaData((prev) => {
          if (!prev) return prev;

          // Si existe facturaParcial
          if (prev.facturaParcial) {
            return {
              ...prev,
              facturaParcial: {
                ...prev.facturaParcial,
                facturaRectificada: response.data.factura.numero,
                facturaRectificadaId: response.data.factura._id,
              },
            };
          }
          // Si existe factura
          if (prev.factura) {
            return {
              ...prev,
              factura: {
                ...prev.factura,
                facturaRectificada: response.data.factura.numero,
                facturaRectificadaId: response.data.factura._id,
              },
            };
          }

          return prev;
        });
        setRectificarFactura(false);
        setEditFactura(false);
        limpiarFormulario();
        setAlert({ open: true, severity: "success", msg: "Factura rectificada correctamente" });
        return;
      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.clear();
          history(constantes.R_HOME);
        } else {
          const error: ILog = {
            method: "FacturarCitaDialog.Component.handleConfirmRectificativa",
            level: "error",
            message: e.message, 
            meta: e.response,
          };
          LogService.logError(error);
          setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
        }
      }
    });
  };

  const handleEditRectificativa = async (devolverSesiones: boolean = false) => {
    const validationErrors = validateFactura({
      tipoPago,
      selectedServicio,
      servicio,
      concepto,
      precio: bonoId ? 1 : precio,
      facturable: true,
      facturaPagada,
      metodoPago,
      isEdit: true
    }); 

    setErrors(validationErrors);  

    if (Object.keys(validationErrors).length > 0) return;

    const total = facturaData?.factura;
    const parcial = facturaData?.facturaParcial;

    const facturaTarget = parcial || total;
    if (!facturaTarget) return;
    
    const payload: IFacturaRectificativaPayload = {
      userId: ConfigUtils.getUserId(),
      pacienteId: pacienteId,
      servicio,
      conceptos: [{
        nombre: concepto,
        cantidad: 1,
        precio: precio
      }],
      impuestos: [{
        tipo: "IVA",
        base: precio,
        cuota: impuesto
      }],
      tipoPago: tipoPago,
      tipoFactura: facturaTarget.tipo,
      metodoPago: metodoPago
      ? {
          ...metodoPago,
          nombre: bonoId && bonoId !== "" ? "Bono" : metodoPago.nombre
        }
      : undefined,
      facturaOriginalId: facturaTarget.facturaOriginalId!,
      bonoId,
      devolverSesiones
    }; 

    runAction(async () => {
      if (!payload) return;
      setConfirmOpen(false);
      
      try {
        const response = await FacturacionService.editFacturaRectificativa(facturaTarget._id, payload);
        setFacturaData((prev) => {
          if (!prev) return prev;
          if (prev.facturaParcial && facturaTarget._id === prev.facturaParcial._id) {
            return { ...prev, facturaParcial: response.data.facturaComplementaria };
          }
        
          if (prev.factura && facturaTarget._id === prev.factura._id) {
            return { ...prev, factura: response.data.facturaComplementaria };
          }
        
          return prev;
        });
        let estadoFacturacion;
        if (response.data.facturaComplementaria.estadoPago === "Pagada") estadoFacturacion = EstadoFacturacion.PAGADA;
        else estadoFacturacion = EstadoFacturacion.PENDIENTE_PAGO;
        if (onRectificativaActualizada) onRectificativaActualizada({ _id: response.data.facturaComplementaria._id, estado: estadoFacturacion, servicio: response.data.facturaComplementaria.servicio, modalidad: "rectificativa", total: response.data.facturaComplementaria.importeTotal, fechaPago: response.data.facturaComplementaria.metodoPago?.fechaPago?.toString() ?? null });
        setEditFactura(false);
        limpiarFormulario();
        return;
      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.clear();
          history(constantes.R_HOME);
        } else {
          const error: ILog = {
            method: "FacturarCitaDialog.Component.handleConfirmRectificativa",
            level: "error",
            message: e.message,
            meta: e.response,
          };
          LogService.logError(error);
          setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
        }
      }
    });

  };

  const runAction = async (fn: () => Promise<void>) => {
    if (loadingAction) return;

    try {
      setLoadingAction(true);
      await fn();
    } finally {
      setLoadingAction(false);
    }
  };

  const debePreguntarDevolucion = (
    pagoOriginalCodigo?: MetodoPagoCodigo,
    metodoActual?: IFacturaMetodoPago,
    bonoOriginalId?: string | null,
    bonoActualId?: string
  ) => {
    const pagoAntesEraBono =
      pagoOriginalCodigo === MetodoPagoCodigo.Otro;

    const metodoActualEsBono =
      metodoActual?.codigo === MetodoPagoCodigo.Otro;

    const bonoCambiado = bonoOriginalId !== bonoActualId;

    return (
      (pagoAntesEraBono && !metodoActualEsBono) ||
      (pagoAntesEraBono && metodoActualEsBono && bonoCambiado)
    );
  };

  const startDate = facturaData?.cita?.start ? new Date(facturaData.cita.start) : null;

  const detalleCita = {
    fecha: startDate
      ? startDate.toLocaleDateString("es-ES")
      : "",

    hora: startDate
      ? startDate.toLocaleTimeString("es-ES", {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "",
  };

  // Mismas reglas que valida el backend en cambiarEntidadFactura: ni rectificativa, ni ya
  // rectificada, ni pagada con bono (la entidad la fija el bono, no se puede reasignar aparte),
  // ni parcial con complementaria ya asociada — se comprueban aquí para no dejar clicar una
  // acción que el servidor va a rechazar igualmente.
  const facturaParaCambiarEntidad = facturaData?.factura ?? facturaData?.facturaParcial;
  const puedeCambiarEntidad = Boolean(
    facturaParaCambiarEntidad &&
    facturaParaCambiarEntidad.estado !== 'Anulada' &&
    !facturaParaCambiarEntidad.facturaOriginalId &&
    !facturaParaCambiarEntidad.facturaRectificadaId &&
    !facturaParaCambiarEntidad.bonoId &&
    !(facturaData?.facturaParcial && facturaData?.facturaComplementaria)
  );

  return (
    <>
      <Dialog open={open} maxWidth="sm" fullWidth onClose={loadingAction ? undefined : onClose}>
        {loadingInit ? (
          <CircularProgress style={{ margin: "20px auto" }} />
        ) : (
          <>
          {rectificarFactura ? 
            <DialogTitle>
            {`Rectificar factura nº: ${numeroFactura}`}
            </DialogTitle>
          : <CabeceraFactura
            esFacturable={(facturaData?.tipoFacturacion! !== "no_facturable" && facturable)}
            estado={facturaData?.factura?.estado}
            paciente={facturaData?.factura?.estado === "Emitida" ? facturaData.factura.receptor : facturaData?.paciente}
            cita={detalleCita}
            pacienteMenor={PacientesUtils.calcularEdad(facturaData?.paciente.fechaNacimiento!) as number < 18 ? `${facturaData?.paciente.nombre} ${facturaData?.paciente.apellidos}` : undefined}
            emisor={facturaData?.factura?.emisor ?? facturaData?.facturaParcial?.emisor}
            onEditarEmisor={
              userRole === constantes.ROLES.ADMINISTRADOR && ConfigUtils.getDatosFacturacionAdicional().length > 0 && puedeCambiarEntidad
                ? () => setModalCambiarEntidad(true)
                : undefined
            }
          />}
          {(!facturaData?.tipoFacturacion || editFactura) || (rectificarFactura) ?
            <DialogContent dividers={rectificarFactura}>
              {!facturaData?.tipoFacturacion && !editFactura && !rectificarFactura &&
                <SelectorEntidadFacturacion
                  value={selectedEntidadId}
                  onChange={(v) => { setSelectedEntidadId(v); setErrors(prev => ({ ...prev, entidad: undefined })); }}
                  disabled={entidadBloqueadaPorBono}
                  error={!!errors.entidad}
                  sx={{ mb: 2 }}
                />
              }
              <FacturarForm
                pacienteId={facturaData?.paciente._id!}
                moneda={rectificarFactura ? (facturaRectificar?.moneda || 'EUR') : (ConfigUtils.getUserCurrency() || 'EUR')}
                tipoPago={tipoPago}
                setTipoPago={setTipoPago}
                selectedServicio={selectedServicio}
                setSelectedServicio={setSelectedServicio}
                facturable={facturable}
                setFacturable={setFacturable}
                motivoNoFacturable={motivoNoFacturable}
                setMotivoNoFacturable={setMotivoNoFacturable}
                servicio={servicio}
                setServicio={setServicio}
                concepto={concepto}
                setConcepto={setConcepto}
                precio={precio}
                setPrecio={setPrecio}
                impuesto={impuesto}
                setImpuesto={setImpuesto}
                precioTotal={precioTotal}
                setPrecioTotal={setPrecioTotal}
                inputPrecioBase={inputPrecioBase}
                setInputPrecioBase={setInputPrecioBase}
                errors={errors}
                setErrors={setErrors}
                confirmNoFacturable={confirmNoFacturable}
                setConfirmNoFacturable={setConfirmNoFacturable}
                facturaPagada={facturaPagada}
                setFacturaPagada={setFacturaPagada}
                metodoPago={metodoPago}
                setMetodoPago={setMetodoPago}
                bonoId={bonoId}
                setBonoId={setBonoId}
                bonoIdOriginal={bonoOriginalId!}
                isEdit={editFactura}
                isRectificar={rectificarFactura}
                numeroFactura={numeroFactura}
                fechaFactura={fechaFactura}
                tieneComplementaria={tieneComplementaria}
                setAlert={setAlert}
                esRectificativa={esRectificativa}
                onBonoSeleccionadoChange={(bono) => {
                  if (bono) {
                    setSelectedEntidadId(bono.entityId ?? null);
                    setEntidadBloqueadaPorBono(true);
                  } else {
                    setEntidadBloqueadaPorBono(false);
                  }
                }}
                facturaEntidadId={
                  editFactura
                    ? (facturaData?.factura?.entityId ?? facturaData?.facturaParcial?.entityId ?? null)
                    : rectificarFactura
                      ? (facturaRectificar?.entityId ?? null)
                      : undefined
                }
              />
            </DialogContent> : 
            (
            <DialogContent>
              {facturaData && facturaData?.tipoFacturacion === 'no_facturable' ? 
              <DetalleNoFacturable 
                id={facturaData.id} 
                motivo={facturaData.motivoNoFacturable} 
                onUpdate={(nuevoMotivo) => {
                  setFacturaData(prev => {
                    if (!prev) return prev;

                    return {
                      ...prev,
                      motivoNoFacturable: nuevoMotivo
                    };
                  });
                  if (onMotivoActualizado) onMotivoActualizado(nuevoMotivo);
                }}
              />  
              : facturaData.tipoFacturacion=== 'total' ?
              <DetalleFactura 
                factura={facturaData.factura!} 
                tipoFacturacion={facturaData?.tipoFacturacion} 
                citaId={facturaData.cita?._id!} 
                pacienteId={facturaData.paciente._id}
                email={facturaData.paciente.email}
                authEmail={facturaData.paciente.authEmail}
                detalleCita={detalleCita}
                onFacturaPagada={(id, f) => {
                  setFacturaData(prev => {
                    if (!prev || !f) return prev;
                    return { ...prev, factura: f.factura };
                  });
                  onServicioActualizado({_id: id, estado: getEstadoFacturacion(f?.factura?.estado, f?.factura?.estadoPago), fechaPago: f?.factura?.metodoPago?.fechaPago?.toString() ?? null});
                }}
                onFacturaEmitida={(id, f) => {
                  setFacturaData(prev => {
                    if (!prev || !f) return prev;
                    return { ...prev, factura: f.factura };
                  });
                  onServicioActualizado({_id: id, estado: getEstadoFacturacion(f?.factura?.estado, f?.factura?.estadoPago), numero: f?.factura?.numero});
                }}
                onRectificarFactura={(f) => { setIsRectificarFactura(f) }}
                onPagarRectificada={(f) => {
                  setFacturaData(prev => {
                    if (!prev || !f) return prev;
                    return { ...prev, factura: f.factura };
                  });
                  if (onRectificativaActualizada) onRectificativaActualizada({_id: f?.factura?._id!, estado: getEstadoFacturacion(f?.factura?.estado, f?.factura?.estadoPago), fechaPago: f?.factura?.metodoPago?.fechaPago?.toString() ?? null});
                }}
                onEmitirRectificada={(f) => {
                  setFacturaData(prev => {
                    if (!prev || !f) return prev;
                    return { ...prev, factura: f.factura };
                  });
                  if (onRectificativaActualizada) onRectificativaActualizada({_id: f?.factura?._id!, estado: getEstadoFacturacion(f?.factura?.estado, f?.factura?.estadoPago)});
                }}
                pacienteFechaNacimiento={!!facturaData.paciente.fechaNacimiento}
                pacienteMenor={PacientesUtils.calcularEdad(facturaData.paciente.fechaNacimiento!) as number < 18 ? `${facturaData.paciente.nombre} ${facturaData.paciente.apellidos}` : undefined}
                onFacturaEliminada={handleFacturaEliminada}
                onEstadoCambiado={recargarPorCambioDeEstado}
                onAnularFactura={(id, motivo, devolverSesiones, anulacion) => {
                  setFacturaData(prev => {
                    if (!prev) return prev;

                    return {
                      ...prev,
                      factura: {
                        ...prev.factura,
                        estado: "Anulada",
                        motivoAnulacion: motivo
                      } as IFacturaData,
                    };
                  });
                  onAnularFactura?.(id, motivo, devolverSesiones, anulacion);
                }}
              /> : 
              <DetalleFacturaParcial 
                facturaParcial={facturaData.facturaParcial!}
                facturaComplementaria={facturaData.facturaComplementaria!}
                tipoFacturacion={facturaData?.tipoFacturacion} 
                citaId={facturaData.cita?._id!} 
                pacienteId={facturaData.paciente._id}
                email={facturaData.paciente.email}
                authEmail={facturaData.paciente.authEmail}
                detalleCita={detalleCita}
                onFacturaPagada={(id, f) => {
                  setFacturaData(prev => {
                    if (!prev || !f) return prev;
                    return { ...prev, facturaParcial: f.factura };
                  });
                  onServicioActualizado({_id: id, estado: getEstadoFacturacion(f?.factura?.estado, f?.factura?.estadoPago), fechaPago: f?.factura?.metodoPago?.fechaPago?.toString() ?? null});
                }}
                onFacturaEmitida={(id, f) => {
                  setFacturaData(prev => {
                  if (!prev || !f) return prev;
                  return { ...prev, facturaParcial: f.factura };
                });
                  onServicioActualizado({_id: id, estado: getEstadoFacturacion(f?.factura?.estado, f?.factura?.estadoPago), numero: f?.factura?.numero});
                }}
                onRectificarFactura={(f) => { setIsRectificarFactura(f) }}
                onPagarRectificada={(f) => {
                  setFacturaData(prev => {
                    if (!prev || !f) return prev;
                    return { ...prev, facturaParcial: f.factura };
                  });
                  if (onRectificativaActualizada) onRectificativaActualizada({_id: f?.factura?._id!, estado: getEstadoFacturacion(f?.factura?.estado, f?.factura?.estadoPago), fechaPago: f?.factura?.metodoPago?.fechaPago?.toString() ?? null});
                }}
                onEmitirRectificada={(f) => {
                  setFacturaData(prev => {
                  if (!prev || !f) return prev;
                  return { ...prev, facturaParcial: f.factura };
                });
                  if (onRectificativaActualizada) onRectificativaActualizada({_id: f?.factura?._id!, estado: getEstadoFacturacion(f?.factura?.estado, f?.factura?.estadoPago)});
                }}
                pacienteFechaNacimiento={!!facturaData.paciente.fechaNacimiento}
                pacienteMenor={PacientesUtils.calcularEdad(facturaData.paciente.fechaNacimiento!) as number < 18 ? `${facturaData.paciente.nombre} ${facturaData.paciente.apellidos}` : undefined}
                onFacturaEliminada={handleFacturaEliminada}
                onEstadoCambiado={recargarPorCambioDeEstado}
                onAnularFactura={(id, motivo, anulacion) => {
                  setFacturaData(prev => {
                    if (!prev) return prev;

                    return {
                      ...prev,
                      facturaParcial: {
                        ...prev.facturaParcial,
                        estado: "Anulada",
                        motivoAnulacion: motivo
                      } as IFacturaData,
                    };
                  });
                  onAnularFactura?.(id, motivo, undefined, anulacion);
                }}
              />
              }
            </DialogContent>
          )
            }
            <DialogActions>
              <Button onClick={onClose} disabled={loadingAction}>
                Cerrar
              </Button>

              {/* Botón "Crear rectificativa" */}
              {userRole === constantes.ROLES.ADMINISTRADOR && rectificarFactura &&
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  const validationErrors = validateFactura({
                    tipoPago,
                    selectedServicio,
                    servicio,
                    concepto,
                    precio: bonoId ? 1 : precio,
                    facturable: true,
                    facturaPagada,
                    metodoPago,
                    isEdit: true
                  });
                  setErrors(validationErrors);
                  if (Object.keys(validationErrors).length > 0) return;

                  const codigoOriginal =
                    facturaRectificar?.metodoPago?.codigo;

                  if (
                    debePreguntarDevolucion(
                      codigoOriginal,
                      metodoPago,
                      bonoOriginalId,
                      bonoId
                    )
                  ) {
                    setAccionPendiente(() => validateRectificativa);
                    setModalConfirm(true);
                  } else {
                    validateRectificativa(false);
                  }
                }}
                size="small"
              >
                Crear rectificativa
              </Button>
              }

              {/* Botón “Editar” para servicios no facturables */}
              {facturaData &&
                facturaData.tipoFacturacion === "no_facturable" &&
                !facturaData.factura &&
                !facturaData.facturaParcial &&
                !facturaData.facturaComplementaria && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleOpenFacturaInfo}
                    size="small"
                  >
                    Facturar
                  </Button>
                )}

              {/* Botón “Facturar” solo si no hay factura activa y es facturable */}
              {facturable && !(
                facturaData?.factura ||
                facturaData?.facturaParcial ||
                facturaData?.facturaComplementaria
              ) && (facturaData?.tipoFacturacion !== "no_facturable") && (
                <Button
                  onClick={handleFacturar}
                  variant="contained"
                  color="primary"
                  disabled={loadingAction}
                  startIcon={loadingAction && <CircularProgress size={18} />}
                >
                  Facturar
                </Button>
              )}

              {/* Botón "Guardar" solo si no hay factura activa y es no facturable */}
              {!facturaData?.tipoFacturacion && !facturable &&
                <Button
                  onClick={handleFacturar}
                  variant="contained"
                  color="primary"
                  disabled={loadingAction}
                  startIcon={loadingAction && <CircularProgress size={18} />}
                >
                  Guardar
                </Button>
              }

              {/* Botón “Editar / Guardar” para cualquier factura activa no emitida */}
              {(() => {
                const facturaActiva =
                  facturaData?.factura ??
                  facturaData?.facturaParcial ??
                  facturaData?.facturaComplementaria;
              
                if (facturaActiva && facturaActiva.estado !== "Emitida") {
                  return (
                    <Button
                      onClick={editFactura ? localRecitificativa ? onClickEditarRectificativa : onClickEditar : handleOpenFacturaInfo}
                      variant="contained"
                      color="primary"
                      size="small"
                      disabled={loadingAction || facturaActiva.estado === "Anulada"}
                      startIcon={loadingAction && editFactura && <CircularProgress size={18} />}
                    >
                      {editFactura ? "Guardar" : "Editar"}
                    </Button>
                  );
                }
                return null;
              })()}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* CONFIRMAR NO FACTURABLE */}
      <Dialog open={confirmNoFacturable} maxWidth="sm" onClose={loadingAction ? undefined : () => setConfirmNoFacturable(false)}>
        <DialogContent>
          <Typography>
            ¿Marcar esta cita como <b>NO facturable</b>? Dejará de aparecer para facturar
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setFacturable(false);
              setConfirmNoFacturable(false);
              setErrors({});
            }}
            disabled={loadingAction}
          >
            Sí, continuar
          </Button>
          <Button onClick={() => setConfirmNoFacturable(false)} disabled={loadingAction}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openFacturaInfo} onClose={() => setOpenFacturaInfo(false)}>
        <DialogTitle>Aviso</DialogTitle>      

        <DialogContent dividers>
          <Typography>{infoMessage}</Typography>
        </DialogContent>      

        <DialogActions>
          <Button variant="contained" onClick={() => setOpenFacturaInfo(false)}>
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => {setConfirmOpen(false); setNuevaFacturaRectificativa(null)}}>
        <DialogTitle>Confirmar creación de rectificativa</DialogTitle>
        <DialogContent>
          <Typography>
            Se creará una rectificativa de la factura nº <b>{facturaRectificar?.numero}</b>. ¿Continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setConfirmOpen(false);
              setNuevaFacturaRectificativa(null);
            }}
          >Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleConfirmRectificativa}
            startIcon={loadingAction && <CircularProgress size={18} />}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {modalCambiarEntidad && (
        <CambiarEntidadDialog
          open={modalCambiarEntidad}
          onClose={() => setModalCambiarEntidad(false)}
          entidadActualId={facturaData?.factura?.entityId ?? facturaData?.facturaParcial?.entityId ?? null}
          esBorrador={(facturaData?.factura ?? facturaData?.facturaParcial)?.estado === "Borrador"}
          loading={loadingCambiarEntidad}
          onConfirm={handleCambiarEntidad}
        />
      )}

      {modalConfirm && (
      <Dialog
        open={modalConfirm}
        onClose={() => {
          setModalConfirm(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmación de bono</DialogTitle>
        <DialogContent>
          <Typography>
            {facturaData?.factura?.metodoPago?.codigo === MetodoPagoCodigo.Otro &&
            metodoPago?.codigo !== MetodoPagoCodigo.Otro
              ? "Esta factura estaba pagada con un bono."
              : "Se cambiará el bono usado en esta factura."}
          </Typography>
          <Typography sx={{ mt: 1, fontWeight: "bold" }}>
            Selecciona qué deseas hacer con las sesiones:
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Button onClick={() => setModalConfirm(false)} color="inherit">
            Cancelar
          </Button>

          <Button
            variant="outlined"
            onClick={() => {
              accionPendiente?.(false);
              setAccionPendiente(null);
              setModalConfirm(false);
            }}
          >
            Editar solo
          </Button>
          
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              accionPendiente?.(true);
              setAccionPendiente(null);
              setModalConfirm(false);
            }}
          >
            Editar y devolver
          </Button>
        </DialogActions>
      </Dialog>
    )}

      <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert variant="filled"  onClose={handleCloseAlert} severity={alert.severity} sx={{ width: "100%" }}>
          {alert.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FacturarCitaDialog;
